import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import type { SandboxInfo } from "@timely-sandbox/core/types";
import { ApiError, deleteSandbox,  } from "./api";
import { at, since, volumeCost } from "./format";
import { Button, Checkbox } from "./ui";

/**
 * 상세 패널 (오른쪽 서랍).
 *
 * 목록은 밀도를 위해 열을 줄이므로, "이 한 건에 대해 아는 전부"는 여기서 본다. 삭제도 여기서
 * 한다 — 목록의 `...` 에서 바로 지우게 하면 잘못된 행을 지우기 쉽고, 무엇을 지우는지(볼륨까지
 * 지울지) 보여줄 자리가 없다.
 *
 * 확인은 **두 번 누르기**다. 이름을 타이핑하게 하는 건 운영자 한 명이 쓰는 콘솔엔 과하고,
 * 한 번 누르기는 미끄러진 클릭으로 남의 샌드박스를 지운다.
 */

/** 볼륨은 더 이상 별도 객체가 아니다 — 샌드박스가 곧 그 디스크다. */
export type Target = { kind: "sandbox"; item: SandboxInfo };

function Row({ k, children }: { k: string; children: ComponentChildren }) {
  return (
    <div class="drow">
      <span class="dk">{k}</span>
      <span class="dv">{children}</span>
    </div>
  );
}

const Labels = ({ labels }: { labels: Record<string, string> }) => {
  const entries = Object.entries(labels ?? {});
  if (entries.length === 0) return <span class="faint">없음</span>;
  return (
    <span class="tags">
      {entries.map(([k, v]) => (
        <span key={k} class="tag">
          {k}={v}
        </span>
      ))}
    </span>
  );
};

export function DetailPanel({
  target,
  canDelete,
  onClose,
  onDeleted,
}: {
  target: Target;
  /** 세션이 삭제 권한을 갖는지. 없으면 버튼 대신 이유를 보여준다. */
  canDelete: boolean;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const name = target.item.id;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // 대상이 바뀌면 무장 상태를 반드시 푼다 — 안 그러면 "삭제" 를 눌러 둔 채 다른 행을 열면
  // 다음 클릭 한 번이 엉뚱한 것을 지운다.
  useEffect(() => {
    setArmed(false);
    setError(undefined);
  }, [name]);

  const remove = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await deleteSandbox(target.item.id);
      onDeleted();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? "이 세션에는 삭제 권한이 없습니다."
          : err instanceof Error
            ? err.message
            : String(err),
      );
      setArmed(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div class="veil" onClick={onClose} />
      <aside class="drawer" role="dialog" aria-label={name}>
        <header class="dhead">
          <span class="dkind">샌드박스</span>
          <Button onClick={onClose}>닫기</Button>
        </header>
        <h3 class="dtitle">{name}</h3>

        <div class="dbody">
            <Row k="워크스페이스">{target.item.workspace ?? <span class="faint">없음 (예비)</span>}</Row>
            <Row k="상태">
              <span class={target.item.state === "claimed" ? "ok" : "dim"}>{target.item.state}</span>
            </Row>
            <Row k="생성">
              {at(target.item.createdAt)} <span class="faint">({since(target.item.createdAt)} 전)</span>
            </Row>
            <Row k="이미지">
              <span class="mono-wrap">{target.item.image}</span>
            </Row>
            <Row k="마지막 활동">
              {target.item.lastActivityAt ? (
                <>
                  {at(target.item.lastActivityAt)}{" "}
                  <span class="faint">({since(target.item.lastActivityAt)} 전)</span>
                </>
              ) : (
                // sandboxd 가 보고하는 값이라, 아직 아무 요청도 안 받은 파드는 null 이다.
                <span class="faint">아직 없음</span>
              )}
            </Row>
            <Row k="유휴 TTL">{target.item.idleTtlSec}s</Row>
            <Row k="최대 수명">
              {target.item.maxAgeSec ? `${target.item.maxAgeSec}s` : <span class="faint">제한 없음</span>}
            </Row>
            <Row k="URL">
              {target.item.url ? (
                <span class="mono-wrap">{target.item.url}</span>
              ) : (
                <span class="faint">파드가 아직 없음</span>
              )}
            </Row>
            <Row k="라벨">
              <Labels labels={target.item.labels} />
            </Row>
        </div>

        <section class="dzone">
          <h4>삭제</h4>
          <p class="dnote">
            샌드박스와 그 디스크가 함께 사라진다 — 파일은 복구되지 않는다.
          </p>
          {!canDelete ? (
            <p class="dnote warn">이 세션에는 삭제 권한이 없다.</p>
          ) : (
            <>
              <p class="dnote warn">
                샌드박스와 그 디스크가 함께 사라진다 — 파일은 복구되지 않는다.
              </p>
              {armed ? (
                <div class="confirm">
                  <Button tone="danger" disabled={busy} onClick={() => void remove()}>
                    {busy ? "지우는 중" : "정말 삭제"}
                  </Button>
                  <Button disabled={busy} onClick={() => setArmed(false)}>
                    취소
                  </Button>
                </div>
              ) : (
                <Button tone="danger" onClick={() => setArmed(true)}>
                  삭제
                </Button>
              )}
            </>
          )}
          {error ? <p class="dnote bad">{error}</p> : null}
        </section>
      </aside>
    </>
  );
}
